import AjustableImage from 'Custom/AjustableImage';
import Duration from 'Custom/Duration';
import { Finder } from 'Custom/Finder';
import RichText from 'Custom/RichText';
import SplitContent from 'Custom/Split/SplitContent';
import ArrowRight from 'Images/brand-icons/arrow-md-right.svg';
import Button from 'Nori/Button';
import Hero from 'Nori/Hero/HeroFull2';
import Layout from 'Nori/Layout';

import styles from 'styles/pages/removalAftercare.module.scss';

import ItemBreakpoint from 'Custom/ItemBreakpoint/index';
import HERO_BG_IMAGE_MOBILE from 'Images/background/blue-bg-mobile.png';
import HERO_BG_IMAGE from 'Images/background/dark-blue-desktop.png';
import CIRCLE_IMAGE_DESKTOP from 'Images/misc/circle-arrows-desktop.webp';
import PLUS_IMAGE_DESKTOP from 'Images/misc/plus-sign-desktop.webp';
import PREG_TEST_IMAGE_DESKTOP from 'Images/misc/preg-test-yellow-desktop.webp';
import Container from 'Nori/Container/index';

import HeroQuestion from 'Custom/HeroQuestion/index';

const HEAD = {
  title:
    'Removal and Aftercare for NEXPLANON® (etonogestrel implant) 68 mg Radiopaque',
  description:
    'Learn what to expect during and after NEXPLANON® (etonogestrel implant) 68 mg Radiopaque removal, including aftercare guidance.',
  keywords:
    'Nexplanon Removal, Nexplanon Removal Side Effects, Removal of Nexplanon',
};

export default function InsertionAftercare() {
  return (
    <Layout head={HEAD}>
      <Hero
        image={HERO_BG_IMAGE}
        mobileImage={HERO_BG_IMAGE_MOBILE}
        className={styles.heroRemovalAftercare}
        data-qa-tag="hero removal after care"
      >
        <Container>
          <h4 className={styles.heroBaseHeading} data-qa-tag="heroBaseHeading">
            REMOVAL & AFTERCARE
          </h4>
          <div className={styles.heroHeading}>
            <div className={styles.middleHeroText} data-qa-tag="middleHeroText">
              getting <span>Nexplanon</span> removed
            </div>
          </div>
          <div className={styles.heroSubheading} data-qa-tag="heroSubheading">
            A trained healthcare professional can remove NEXPLANON at any time
            during the 5-year period.<sup>†</sup>
          </div>
          <div className={styles.heroInfoPara}>
            <sup>†</sup>NEXPLANON must be removed by the end of the fifth year.
          </div>
        </Container>
      </Hero>
      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="Getting NEXPLANON removed"
        data-design-module_no="02"
        className={styles.sectionGettingNxRemoved}
        data-qa-tag="section Getting NEXPLANON removed"
      >
        <ItemBreakpoint breakpoint="lg">
          <AjustableImage
            size="full"
            image={{
              src: CIRCLE_IMAGE_DESKTOP,
              style: { width: 292 },
              alt: '',
            }}
          />
          <AjustableImage
            size="full"
            image={{
              src: CIRCLE_IMAGE_DESKTOP,
              style: { width: 146 },
              alt: '',
            }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext Getting NEXPLANON removed">
            <h2 data-qa-tag="title Getting NEXPLANON removed">
              Getting NEXPLANON removed
            </h2>
            <p data-qa-tag="body Getting NEXPLANON removed">
              Removal of NEXPLANON involves a minor surgical procedure by a
              trained healthcare professional and can be performed in the office
              through a small incision in your arm where NEXPLANON is located.
            </p>
          </RichText>
        </>
      </SplitContent>

      <SplitContent
        reverse
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="Healing from the removal procedure"
        data-design-module_no="03"
        className={styles.sectionHealingRemoval}
        data-qa-tag="section Healing from the removal procedure"
      >
        <ItemBreakpoint breakpoint="lg">
          <AjustableImage
            size="full"
            image={{ src: PLUS_IMAGE_DESKTOP, style: { width: 292 }, alt: '' }}
          />
          <AjustableImage
            size="full"
            image={{ src: PLUS_IMAGE_DESKTOP, style: { width: 146 }, alt: '' }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext Healing from the removal procedure">
            <h2 data-qa-tag="title Healing from the removal procedure">
              Healing from the removal procedure
            </h2>
            <p data-qa-tag="Healing from a">
              Women may have discomfort and some bruising following NEXPLANON
              removal.
            </p>
            <p data-qa-tag="Healing from b">
              Similar to when you get NEXPLANON, you will want to wear your
              pressure bandage for a full 24 hours and leave your adhesive
              bandage on for 3-5 days.
            </p>
            <p data-qa-tag="Healing from c">
              For complete removal care instructions, you may get details by
              following the link below.
            </p>
            <Button
              type="simple"
              href="#removal-aftercare"
              data-design-type="clickAction"
              data-design-category="content_link_vd"
              data-design-action="Link-Anchor"
              data-design-label="Details on removal care"
              data-design-nori_id="Inline Content"
              data-design-module_name="healing from the removal procedure"
              data-design-module_no="03"
              data-qa-tag="button Details on removal care"
            >
              Details on removal care <ArrowRight />
            </Button>
          </RichText>
        </>
      </SplitContent>

      <Duration
        title="Removal Aftercare"
        anchorID="removal-aftercare"
        className={styles.duration}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="removal aftercare"
        data-design-module_no="04"
        data-qa-tag="wrapper Your NEXPLANON implant was just removed"
        enableLearnMoreBtn={false}
      >
        <h2 data-qa-tag="title Your NEXPLANON implant was just removed">
          Your NEXPLANON implant was just removed
        </h2>
        <p
          className="intro"
          data-qa-tag="intro Your NEXPLANON implant was just removed"
        >
          Here is some helpful information on what you can expect and how to
          care for your removal site.
        </p>
      </Duration>

      <SplitContent
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="getting pregnant after NEXPLANON"
        data-design-module_no="05"
        className={styles.sectionGettingPGAfterNX}
        data-qa-tag="section Getting pregnant after NEXPLANON"
      >
        <ItemBreakpoint breakpoint="lg">
          <AjustableImage
            size="full"
            image={{
              src: PREG_TEST_IMAGE_DESKTOP,
              style: { width: 292 },
              alt: '',
            }}
          />
          <AjustableImage
            size="full"
            image={{
              src: PREG_TEST_IMAGE_DESKTOP,
              style: { width: 146 },
              alt: '',
            }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="richtext Getting pregnant after NEXPLANON">
            <h2 data-qa-tag="title Getting pregnant after NEXPLANON">
              Getting pregnant after NEXPLANON
            </h2>
            <p data-qa-tag="Getting pregnant a">
              You may become pregnant as early as a week after the removal of
              NEXPLANON.
            </p>
            <p data-qa-tag="Getting pregnant b">
              After NEXPLANON is removed, and if you do not wish to get pregnant
              at this time, you should start another birth control method, such
              as condoms, right away.
            </p>
            <p data-qa-tag="Getting pregnant c">
              If you want to get pregnant, please consult with your doctor.
            </p>
          </RichText>
          <Button
            type="simple"
            href="/pregnancy-after-nexplanon/"
            data-design-type="clickAction"
            data-design-category="content_link_vd"
            data-design-action="Link-Anchor"
            data-design-label="See more about pregnancy after NEXPLANON"
            data-design-nori_id="Inline Content"
            data-design-module_name="getting pregnant after NEXPLANON"
            data-design-module_no="05"
            data-qa-tag="button See more about pregnancy after NEXPLANON"
          >
            See more about pregnancy after NEXPLANON <ArrowRight />
          </Button>
        </>
      </SplitContent>
      <HeroQuestion></HeroQuestion>
      <Finder data-design-module_no="07" />
    </Layout>
  );
}
